/**
 * PORTED — RO-3-03 / #44296.
 * Origin: `excellent/app/planner/page.tsx`.
 *
 * Changes:
 *   listAllScopedTasksWithDueDates / Unscheduled → `useApi(modes.planner)`
 *   setDueDate → `apiCall(taskActions.setDueDate)`
 *   metadata → dropped
 */

import { BoundedScroll, Planner, StatStrip, type StatStripItem } from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { taskActions } from "@excellent/ui/api/contracts/task-actions";
import type { ScopedTask, ViewScope } from "@/projects/types";

export function PlannerPage({
  searchParams,
}: {
  searchParams?: Record<string, string | undefined>;
}) {
  const month = searchParams?.month;
  const { data, isLoading, error, mutate } = useApi(modes.planner, {
    query: month ? { month } : {},
  });

  if (error) {
    return (
      <p className="px-6 type-body text-destructive">
        {error.message ?? "Failed to load planner."}
      </p>
    );
  }
  if (isLoading || !data) {
    return <p className="px-6 type-body text-muted-foreground">Loading…</p>;
  }

  const scheduled = data.scheduled as ScopedTask[];
  const unscheduled = data.unscheduled as ScopedTask[];
  const overdue = data.overdue;
  const monthIso = data.monthIso;
  const scope: ViewScope = { kind: "workspace" };

  const stats: StatStripItem[] = [
    {
      title: "Scheduled",
      value: String(scheduled.length),
      description: "Issues with a due date",
    },
    {
      title: "Unscheduled",
      value: String(unscheduled.length),
      description: "Waiting to be planned",
    },
    {
      title: "Overdue",
      value: String(overdue),
      footer:
        overdue > 0
          ? [{ count: overdue, label: "past due", intent: "danger" }]
          : undefined,
      description: overdue > 0 ? undefined : "On track",
    },
  ];

  return (
    <div className="flex flex-col">
      <div className="@container/page flex flex-col gap-8">
        <StatStrip items={stats} variant="standalone" />
        <BoundedScroll mode="scroll" offset="24rem">
          <Planner
            scope={scope}
            monthIso={monthIso}
            scheduled={scheduled}
            unscheduled={unscheduled}
            onSetDueDate={async (_scope, taskId, dueDate) => {
              const task =
                scheduled.find((t) => t.id === taskId) ??
                unscheduled.find((t) => t.id === taskId);
              if (!task) return;
              await apiCall(taskActions.setDueDate, {
                body: {
                  projectSlug: task.project_slug,
                  sectionSlug: task.section_slug,
                  taskId,
                  dueDate,
                },
              });
              void mutate();
            }}
          />
        </BoundedScroll>
      </div>
    </div>
  );
}
